import { useState, memo } from "react";

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  priority?: boolean;
  sizes?: string;
  aspectRatio?: string;
}

const CLOUDINARY_CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;

/**
 * Generates a Cloudinary fetch URL for any public image
 * Uses Cloudinary's fetch feature to automatically optimize images
 */
const getCloudinaryUrl = (
  originalUrl: string,
  width?: number,
  quality: string = "auto"
): string => {
  if (!CLOUDINARY_CLOUD_NAME || !originalUrl) {
    return originalUrl;
  }

  // Convert relative URLs to absolute URLs
  let absoluteUrl = originalUrl;
  if (originalUrl.startsWith("/")) {
    absoluteUrl = `${window.location.origin}${originalUrl}`;
  }

  // Build transformation string
  const transforms = [`f_auto`, `q_${quality}`];
  if (width) {
    transforms.push(`w_${width}`);
  }

  const transformString = transforms.join(",");
  
  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/fetch/${transformString}/${encodeURIComponent(absoluteUrl)}`;
};

const OptimizedImage = memo(({
  src,
  alt,
  width,
  height,
  className = "",
  priority = false,
  sizes,
  aspectRatio,
}: OptimizedImageProps) => {
  const [hasError, setHasError] = useState(false);

  // Use original URL if Cloudinary fails or isn't configured
  const optimizedSrc = hasError ? src : getCloudinaryUrl(src, width);

  const handleError = () => {
    if (!hasError) {
      setHasError(true);
    }
  };

  return (
    <img
      src={optimizedSrc}
      alt={alt}
      width={width}
      height={height}
      className={className}
      loading={priority ? "eager" : "lazy"}
      decoding={priority ? "sync" : "async"}
      sizes={sizes}
      style={aspectRatio ? { aspectRatio } : undefined}
      onError={handleError}
      {...(priority ? { fetchpriority: "high" } : {})}
    />
  );
});

OptimizedImage.displayName = "OptimizedImage";

export { OptimizedImage, getCloudinaryUrl };
